import { defineQuery, addEntity, removeEntity } from 'bitecs';
import { Animal, Plant, Position, Tile, Corpse, animalQuery, plantQuery, corpseQuery, tileQuery } from '../world.js';
import { TILE_SIZE, CHUNK_SIZE, CURRENT_MODE, MAP_WIDTH, MAP_HEIGHT } from '../config.js';

/**
 * Ecosystem Simulation System
 * 
 * A comprehensive simulation system for testing animal equilibria with analytics.
 * Allows placing animals with rules and monitoring their population dynamics.
 */
export class SimulationSystem {
    constructor(world, app) {
        this.world = world;
        this.app = app;
        this.isRunning = false;
        this.simulationSpeed = 1.0;
        
        // Simulation parameters
        this.parameters = {
            // Population limits per 1000 tiles
            maxPopulationDensity: {
                RABBIT: 0.8,
                CHICKEN: 0.6,
                WOLF: 0.1,
                HAWK: 0.05,
                DEER: 0.4,
                GOAT: 0.3
            },
            
            // Reproduction rates (per year)
            reproductionRate: {
                RABBIT: 6,
                CHICKEN: 12,
                WOLF: 1,
                HAWK: 1,
                DEER: 1,
                GOAT: 2
            },
            
            // Mortality rates (per year)
            naturalMortality: {
                RABBIT: 0.7,
                CHICKEN: 0.5,
                WOLF: 0.3,
                HAWK: 0.2,
                DEER: 0.4,
                GOAT: 0.35
            },
            
            // Predation success rates
            predationSuccess: {
                WOLF_RABBIT: 0.3,
                WOLF_DEER: 0.15,
                HAWK_CHICKEN: 0.4,
                WOLF_GOAT: 0.2
            },
            
            // Energy parameters
            energyFromFood: {
                PLANT: 15,
                MEAT: 40,
                BERRIES: 10
            },
            
            energyCosts: {
                MOVEMENT: 0.5,
                REPRODUCTION: 30,
                HUNTING: 2,
                RESTING: 0.1
            }
        };
        
        // Analytics data
        this.analytics = {
            populationHistory: [],
            birthDeaths: [],
            predationEvents: [],
            environmentalEvents: [],
            resourceAvailability: [],
            equilibriumState: null
        };
        
        // Simulation state
        this.tickCount = 0;
        this.yearCount = 0;
        this.lastCensus = 0;
        this.censusInterval = 100; // Ticks between census
        
        // UI elements
        this.createSimulationUI();
        
        // Start simulation loop
        this.startSimulation();
    }
    
    createSimulationUI() {
        // Create simulation control panel
        this.createControlPanel();
        
        // Create analytics display
        this.createAnalyticsDisplay();
        
        // Create species placement tools
        this.createPlacementTools();
    }
    
    createControlPanel() {
        // Create simulation control container
        this.controlPanel = new Container();
        this.controlPanel.zIndex = 10000;
        this.app.stage.addChild(this.controlPanel);
        
        // Background for control panel
        const bg = new Graphics();
        bg.fill({ color: 0x0A0A0A, alpha: 0.9 });
        bg.rect(10, 10, 300, 200);
        bg.setStrokeStyle({ width: 2, color: 0x00FF00 });
        bg.rect(10, 10, 300, 200);
        this.controlPanel.addChild(bg);
        
        // Title
        const title = new Text('SIMULATION CONTROL', {
            fontFamily: '"Press Start 2P", monospace',
            fontSize: 12,
            fill: 0x00FFFF,
            letterSpacing: 1
        });
        title.x = 160;
        title.y = 25;
        title.anchor.set(0.5);
        this.controlPanel.addChild(title);
        
        // Control buttons
        this.createControlButtons();
        
        // Parameter displays
        this.createParameterDisplays();
    }
    
    createControlButtons() {
        const buttonStyle = {
            fontFamily: '"Press Start 2P", monospace',
            fontSize: 10,
            fill: 0x00FF00
        };
        
        // Start/Stop button
        this.startStopBtn = this.createPixelButton('START', 50, 60, 80, 30, buttonStyle);
        this.startStopBtn.on('pointerdown', () => this.toggleSimulation());
        this.controlPanel.addChild(this.startStopBtn);
        
        // Reset button
        this.resetBtn = this.createPixelButton('RESET', 150, 60, 80, 30, buttonStyle);
        this.resetBtn.on('pointerdown', () => this.resetSimulation());
        this.controlPanel.addChild(this.resetBtn);
        
        // Speed controls
        this.speedUpBtn = this.createPixelButton('SPEED+', 50, 100, 80, 30, buttonStyle);
        this.speedUpBtn.on('pointerdown', () => this.changeSpeed(0.5));
        this.controlPanel.addChild(this.speedUpBtn);
        
        this.speedDownBtn = this.createPixelButton('SPEED-', 150, 100, 80, 30, buttonStyle);
        this.speedDownBtn.on('pointerdown', () => this.changeSpeed(-0.5));
        this.controlPanel.addChild(this.speedDownBtn);
        
        // Analytics button
        this.analyticsBtn = this.createPixelButton('ANALYTICS', 100, 140, 80, 30, buttonStyle);
        this.analyticsBtn.on('pointerdown', () => this.showAnalytics());
        this.controlPanel.addChild(this.analyticsBtn);
    }
    
    createPixelButton(text, x, y, width, height, style) {
        const button = new Container();
        
        // Background
        const bg = new Graphics();
        bg.fill({ color: 0x0F0F0F });
        bg.rect(0, 0, width, height);
        bg.setStrokeStyle({ width: 1, color: 0x00FF00 });
        bg.rect(0, 0, width, height);
        button.addChild(bg);
        
        // Text
        const label = new Text(text, style);
        label.anchor.set(0.5);
        label.x = width / 2;
        label.y = height / 2;
        button.addChild(label);
        
        // Position
        button.x = x;
        button.y = y;
        
        // Make interactive
        button.eventMode = 'static';
        button.cursor = 'pointer';
        
        // Hover effect
        button.on('pointerover', () => {
            bg.fill({ color: 0x00FF00, alpha: 0.2 });
            bg.rect(0, 0, width, height);
            label.style.fill = 0x00FFFF;
        });
        
        button.on('pointerout', () => {
            bg.fill({ color: 0x000000, alpha: 0 });
            bg.rect(0, 0, width, height);
            label.style.fill = 0x00FF00;
        });
        
        return button;
    }
    
    createParameterDisplays() {
        const displayStyle = {
            fontFamily: '"Courier New", monospace',
            fontSize: 9,
            fill: 0x00FF00
        };
        
        // Simulation speed display
        this.speedDisplay = new Text(`Speed: ${this.simulationSpeed}x`, displayStyle);
        this.speedDisplay.x = 20;
        this.speedDisplay.y = 180;
        this.controlPanel.addChild(this.speedDisplay);
        
        // Tick count display
        this.tickDisplay = new Text(`Tick: 0`, displayStyle);
        this.tickDisplay.x = 120;
        this.tickDisplay.y = 180;
        this.controlPanel.addChild(this.tickDisplay);
        
        // Year display
        this.yearDisplay = new Text(`Year: 0`, displayStyle);
        this.yearDisplay.x = 200;
        this.yearDisplay.y = 180;
        this.controlPanel.addChild(this.yearDisplay);
    }
    
    createAnalyticsDisplay() {
        // Create analytics panel
        this.analyticsPanel = new Container();
        this.analyticsPanel.zIndex = 9999;
        this.analyticsPanel.visible = false;
        this.app.stage.addChild(this.analyticsPanel);
        
        // Background
        const bg = new Graphics();
        bg.fill({ color: 0x0A0A0A, alpha: 0.95 });
        bg.rect(320, 10, 400, 500);
        bg.setStrokeStyle({ width: 2, color: 0x00FFFF });
        bg.rect(320, 10, 400, 500);
        this.analyticsPanel.addChild(bg);
        
        // Title
        const title = new Text('ECOSYSTEM ANALYTICS', {
            fontFamily: '"Press Start 2P", monospace',
            fontSize: 14,
            fill: 0x00FFFF,
            letterSpacing: 1
        });
        title.x = 520;
        title.y = 30;
        title.anchor.set(0.5);
        this.analyticsPanel.addChild(title);
        
        // Population chart area
        this.createPopulationChart();
        
        // Statistics display
        this.createStatisticsDisplay();
        
        // Close button
        const closeBtn = this.createPixelButton('CLOSE', 640, 460, 60, 30, {
            fontFamily: '"Press Start 2P", monospace',
            fontSize: 10,
            fill: 0x00FF00
        });
        closeBtn.on('pointerdown', () => this.hideAnalytics());
        this.analyticsPanel.addChild(closeBtn);
    }
    
    createPopulationChart() {
        // Chart background
        const chartBg = new Graphics();
        chartBg.fill({ color: 0x000000, alpha: 0.5 });
        chartBg.rect(340, 60, 360, 200);
        chartBg.setStrokeStyle({ width: 1, color: 0x00FF00 });
        chartBg.rect(340, 60, 360, 200);
        this.analyticsPanel.addChild(chartBg);
        
        // Chart title
        const chartTitle = new Text('Population Dynamics', {
            fontFamily: '"Courier New", monospace',
            fontSize: 10,
            fill: 0x00FF00
        });
        chartTitle.x = 520;
        chartTitle.y = 70;
        chartTitle.anchor.set(0.5);
        this.analyticsPanel.addChild(chartTitle);
        
        // Population lines will be drawn here
        this.populationLines = new Graphics();
        this.analyticsPanel.addChild(this.populationLines);
    }
    
    createStatisticsDisplay() {
        const statsStyle = {
            fontFamily: '"Courier New", monospace',
            fontSize: 9,
            fill: 0x00FF00
        };
        
        // Statistics text
        this.statsText = new Text('', statsStyle);
        this.statsText.x = 340;
        this.statsText.y = 280;
        this.analyticsPanel.addChild(this.statsText);
        
        // Equilibrium indicator
        this.equilibriumText = new Text('', statsStyle);
        this.equilibriumText.x = 340;
        this.equilibriumText.y = 420;
        this.analyticsPanel.addChild(this.equilibriumText);
    }
    
    createPlacementTools() {
        // Create species placement panel
        this.placementPanel = new Container();
        this.placementPanel.zIndex = 9998;
        this.app.stage.addChild(this.placementPanel);
        
        // Background
        const bg = new Graphics();
        bg.fill({ color: 0x0A0A0A, alpha: 0.9 });
        bg.rect(10, 220, 300, 150);
        bg.setStrokeStyle({ width: 2, color: 0x00FF00 });
        bg.rect(10, 220, 300, 150);
        this.placementPanel.addChild(bg);
        
        // Title
        const title = new Text('SPECIES PLACEMENT', {
            fontFamily: '"Press Start 2P", monospace',
            fontSize: 10,
            fill: 0x00FFFF,
            letterSpacing: 1
        });
        title.x = 160;
        title.y = 235;
        title.anchor.set(0.5);
        this.placementPanel.addChild(title);
        
        // Species buttons
        this.createSpeciesButtons();
    }
    
    createSpeciesButtons() {
        const species = ['RABBIT', 'CHICKEN', 'WOLF', 'HAWK', 'DEER', 'GOAT'];
        const buttonStyle = {
            fontFamily: '"Press Start 2P", monospace',
            fontSize: 8,
            fill: 0x00FF00
        };
        
        species.forEach((speciesName, index) => {
            const x = 20 + (index % 3) * 95;
            const y = 260 + Math.floor(index / 3) * 40;
            
            const button = this.createPixelButton(speciesName, x, y, 85, 30, buttonStyle);
            
            button.on('pointerdown', () => {
                this.selectedSpecies = speciesName;
                console.log(`[SIMULATION] Selected species for placement: ${speciesName}`);
            });
            
            this.placementPanel.addChild(button);
        });
        
        // Placement mode toggle
        this.placementModeBtn = this.createPixelButton('PLACE MODE: OFF', 100, 330, 100, 30, {
            fontFamily: '"Press Start 2P", monospace',
            fontSize: 8,
            fill: 0x888888
        });
        
        this.placementModeBtn.on('pointerdown', () => {
            this.togglePlacementMode();
        });
        
        this.placementPanel.addChild(this.placementModeBtn);
    }
    
    startSimulation() {
        this.isRunning = true;
        console.log('[SIMULATION] Ecosystem simulation started');
        
        // Start simulation loop
        this.app.ticker.add(this.update, this);
    }
    
    update(deltaTime) {
        if (!this.isRunning) return;
        
        // Update simulation speed
        const adjustedDelta = deltaTime * this.simulationSpeed;
        
        this.tickCount++;
        
        // Update displays
        this.updateDisplays();
        
        // Conduct census periodically
        if (this.tickCount - this.lastCensus >= this.censusInterval) {
            this.conductCensus();
            this.lastCensus = this.tickCount;
        }
        
        // Update year (365 ticks = 1 year)
        if (this.tickCount % 365 === 0) {
            this.yearCount++;
            this.processYearlyEvents();
        }
        
        // Process ecosystem
        this.processEcosystem(adjustedDelta);
    }
    
    processEcosystem(deltaTime) {
        // Process animals
        const animals = animalQuery(this.world);
        
        for (const animal of animals) {
            this.processAnimal(animal, deltaTime);
        }
        
        // Process plants
        const plants = plantQuery(this.world);
        
        for (const plant of plants) {
            this.processPlant(plant, deltaTime);
        }
        
        // Process interactions
        this.processInteractions(animals);
    }
    
    processAnimal(animal, deltaTime) {
        const species = this.getAnimalSpecies(Animal.type[animal]);
        const energy = Animal.energy[animal];
        
        // Energy decay
        Animal.energy[animal] -= this.parameters.energyCosts.RESTING * deltaTime;
        
        // Movement
        if (energy > 30 && Math.random() < 0.1 * deltaTime) {
            this.moveAnimal(animal);
            Animal.energy[animal] -= this.parameters.energyCosts.MOVEMENT;
        }
        
        // Foraging
        if (energy < 70 && Math.random() < 0.05 * deltaTime) {
            this.forageAnimal(animal, species);
        }
        
        // Reproduction
        if (energy > 80 && Math.random() < this.getReproductionChance(species) * deltaTime) {
            this.reproduceAnimal(animal, species);
        }
        
        // Death
        if (energy <= 0 || Math.random() < this.getDeathChance(species) * deltaTime) {
            this.killAnimal(animal, species);
        }
    }
    
    processPlant(plant, deltaTime) {
        const species = this.getPlantSpecies(Plant.type[plant]);
        
        // Growth
        if (Plant.growth && Plant.growth[plant] !== undefined) {
            Plant.growth[plant] = Math.min(Plant.growth[plant] + 0.001 * deltaTime, 1.0);
        }
        
        // Reproduction (spreading)
        if (Math.random() < 0.01 * deltaTime) {
            this.spreadPlant(plant, species);
        }
    }
    
    processInteractions(animals) {
        // Predation
        for (let i = 0; i < animals.length; i++) {
            for (let j = 0; j < animals.length; j++) {
                if (i !== j) {
                    this.checkPredation(animals[i], animals[j]);
                }
            }
        }
    }
    
    checkPredation(predator, prey) {
        const predatorSpecies = this.getAnimalSpecies(Animal.type[predator]);
        const preySpecies = this.getAnimalSpecies(Animal.type[prey]);
        
        const predationKey = `${predatorSpecies}_${preySpecies}`;
        const successRate = this.parameters.predationSuccess[predationKey];
        
        if (successRate && Math.random() < successRate * 0.01) {
            // Successful predation
            Animal.energy[predator] += this.parameters.energyFromFood.MEAT;
            this.killAnimal(prey, preySpecies);
            
            // Record predation event
            this.analytics.predationEvents.push({
                tick: this.tickCount,
                predator: predatorSpecies,
                prey: preySpecies,
                location: { x: Animal.x[predator], y: Animal.y[predator] }
            });
            
            console.log(`[SIMULATION] ${predatorSpecies} hunted ${preySpecies}`);
        }
    }
    
    moveAnimal(animal) {
        const moveDistance = TILE_SIZE * 2;
        Animal.x[animal] += (Math.random() - 0.5) * moveDistance;
        Animal.y[animal] += (Math.random() - 0.5) * moveDistance;
    }
    
    forageAnimal(animal, species) {
        const foodGain = this.parameters.energyFromFood.PLANT;
        Animal.energy[animal] += foodGain;
        
        console.log(`[SIMULATION] ${species} found food`);
    }
    
    reproduceAnimal(animal, species) {
        const cost = this.parameters.energyCosts.REPRODUCTION;
        
        if (Animal.energy[animal] > cost) {
            Animal.energy[animal] -= cost;
            
            // Create offspring
            const offspring = addEntity(this.world);
            Animal.type[offspring] = Animal.type[animal];
            Animal.energy[offspring] = 50;
            Animal.x[offspring] = Animal.x[animal] + (Math.random() - 0.5) * TILE_SIZE * 4;
            Animal.y[offspring] = Animal.y[animal] + (Math.random() - 0.5) * TILE_SIZE * 4;
            
            // Record birth
            this.analytics.birthDeaths.push({
                tick: this.tickCount,
                type: 'birth',
                species: species
            });
            
            console.log(`[SIMULATION] ${species} reproduced`);
        }
    }
    
    killAnimal(animal, species) {
        removeEntity(this.world, animal);
        
        // Record death
        this.analytics.birthDeaths.push({
            tick: this.tickCount,
            type: 'death',
            species: species
        });
        
        console.log(`[SIMULATION] ${species} died`);
    }
    
    spreadPlant(plant, species) {
        // Create new plant nearby
        const newPlant = addEntity(this.world);
        Plant.type[newPlant] = Plant.type[plant];
        Plant.growth[newPlant] = 0.1;
        Plant.x[newPlant] = Plant.x[plant] + (Math.random() - 0.5) * TILE_SIZE * 8;
        Plant.y[newPlant] = Plant.y[plant] + (Math.random() - 0.5) * TILE_SIZE * 8;
        
        console.log(`[SIMULATION] ${species} spread to new location`);
    }
    
    conductCensus() {
        const animals = animalQuery(this.world);
        const plants = plantQuery(this.world);
        
        const populationCounts = {};
        
        // Count animals by species
        for (const animal of animals) {
            const species = this.getAnimalSpecies(Animal.type[animal]);
            populationCounts[species] = (populationCounts[species] || 0) + 1;
        }
        
        // Count plants by species
        for (const plant of plants) {
            const species = this.getPlantSpecies(Plant.type[plant]);
            populationCounts[species] = (populationCounts[species] || 0) + 1;
        }
        
        // Store population data
        this.analytics.populationHistory.push({
            tick: this.tickCount,
            year: this.yearCount,
            populations: { ...populationCounts }
        });
        
        // Keep only last 100 data points
        if (this.analytics.populationHistory.length > 100) {
            this.analytics.populationHistory.shift();
        }
        
        // Check equilibrium
        this.checkEquilibrium(populationCounts);
        
        console.log(`[SIMULATION] Census - Populations:`, populationCounts);
    }
    
    checkEquilibrium(populations) {
        // Simple equilibrium check: stable populations for 3 consecutive census
        if (this.analytics.populationHistory.length >= 3) {
            const recent = this.analytics.populationHistory.slice(-3);
            let isStable = true;
            
            for (const species in populations) {
                const counts = recent.map(h => h.populations[species] || 0);
                const variance = this.calculateVariance(counts);
                
                if (variance > populations[species] * 0.1) { // 10% variance threshold
                    isStable = false;
                    break;
                }
            }
            
            if (isStable) {
                this.analytics.equilibriumState = {
                    achieved: true,
                    tick: this.tickCount,
                    year: this.yearCount,
                    populations: { ...populations }
                };
            } else {
                this.analytics.equilibriumState = null;
            }
        }
    }
    
    calculateVariance(numbers) {
        const mean = numbers.reduce((a, b) => a + b, 0) / numbers.length;
        const squaredDiffs = numbers.map(n => Math.pow(n - mean, 2));
        return squaredDiffs.reduce((a, b) => a + b, 0) / numbers.length;
    }
    
    processYearlyEvents() {
        console.log(`[SIMULATION] Year ${this.yearCount} completed`);
        
        // Environmental events
        if (Math.random() < 0.3) {
            this.triggerEnvironmentalEvent();
        }
        
        // Population pressure events
        this.checkPopulationPressure();
    }
    
    triggerEnvironmentalEvent() {
        const events = ['drought', 'abundant_rain', 'disease', 'migration'];
        const event = events[Math.floor(Math.random() * events.length)];
        
        this.analytics.environmentalEvents.push({
            tick: this.tickCount,
            year: this.yearCount,
            event: event
        });
        
        console.log(`[SIMULATION] Environmental event: ${event}`);
        
        // Apply event effects
        this.applyEnvironmentalEffects(event);
    }
    
    applyEnvironmentalEffects(event) {
        const animals = animalQuery(this.world);
        
        switch (event) {
            case 'drought':
                // Reduce plant growth, increase animal energy loss
                for (const animal of animals) {
                    Animal.energy[animal] *= 0.8;
                }
                break;
                
            case 'abundant_rain':
                // Increase plant growth, boost animal energy
                for (const animal of animals) {
                    Animal.energy[animal] *= 1.2;
                }
                break;
                
            case 'disease':
                // Random deaths
                for (const animal of animals) {
                    if (Math.random() < 0.1) {
                        this.killAnimal(animal, this.getAnimalSpecies(Animal.type[animal]));
                    }
                }
                break;
                
            case 'migration':
                // Add new animals
                this.addMigratingAnimals();
                break;
        }
    }
    
    addMigratingAnimals() {
        const species = ['RABBIT', 'DEER', 'GOAT'];
        const migratingSpecies = species[Math.floor(Math.random() * species.length)];
        
        // Add 5-10 migrating animals
        const count = Math.floor(Math.random() * 6) + 5;
        
        for (let i = 0; i < count; i++) {
            const animal = addEntity(this.world);
            Animal.type[animal] = this.getAnimalTypeId(migratingSpecies);
            Animal.energy[animal] = 60;
            Animal.x[animal] = Math.random() * MAP_WIDTH;
            Animal.y[animal] = Math.random() * MAP_HEIGHT;
        }
        
        console.log(`[SIMULATION] ${count} ${migratingSpecies} migrated to the island`);
    }
    
    checkPopulationPressure() {
        const animals = animalQuery(this.world);
        const totalAnimals = animals.length;
        
        // Check if population exceeds carrying capacity
        const maxCapacity = 100; // Arbitrary capacity
        if (totalAnimals > maxCapacity) {
            // Apply pressure effects
            const excess = totalAnimals - maxCapacity;
            const pressureFactor = 1 + (excess / maxCapacity) * 0.5;
            
            for (const animal of animals) {
                Animal.energy[animal] /= pressureFactor;
            }
            
            console.log(`[SIMULATION] Population pressure: ${totalAnimals}/${maxCapacity}`);
        }
    }
    
    updateDisplays() {
        this.tickDisplay.text = `Tick: ${this.tickCount}`;
        this.yearDisplay.text = `Year: ${this.yearCount}`;
        this.speedDisplay.text = `Speed: ${this.simulationSpeed}x`;
    }
    
    // Helper methods
    getAnimalSpecies(typeId) {
        const speciesMap = {
            1: 'RABBIT',
            2: 'CHICKEN', 
            3: 'WOLF',
            4: 'HAWK',
            5: 'DEER',
            6: 'GOAT'
        };
        return speciesMap[typeId] || 'UNKNOWN';
    }
    
    getAnimalTypeId(species) {
        const typeMap = {
            'RABBIT': 1,
            'CHICKEN': 2,
            'WOLF': 3,
            'HAWK': 4,
            'DEER': 5,
            'GOAT': 6
        };
        return typeMap[species] || 1;
    }
    
    getPlantSpecies(typeId) {
        const speciesMap = {
            1: 'TREE',
            2: 'BUSH',
            3: 'FLOWER',
            4: 'MUSHROOM',
            5: 'HERB',
            6: 'CACTUS'
        };
        return speciesMap[typeId] || 'UNKNOWN';
    }
    
    getReproductionChance(species) {
        return this.parameters.reproductionRate[species] * 0.001;
    }
    
    getDeathChance(species) {
        return this.parameters.naturalMortality[species] * 0.0001;
    }
    
    // Control methods
    toggleSimulation() {
        this.isRunning = !this.isRunning;
        this.startStopBtn.children[1].text = this.isRunning ? 'STOP' : 'START';
        console.log(`[SIMULATION] ${this.isRunning ? 'Started' : 'Stopped'} simulation`);
    }
    
    resetSimulation() {
        // Clear all entities
        const animals = animalQuery(this.world);
        const plants = plantQuery(this.world);
        
        for (const animal of animals) {
            removeEntity(this.world, animal);
        }
        
        for (const plant of plants) {
            removeEntity(this.world, plant);
        }
        
        // Reset analytics
        this.analytics = {
            populationHistory: [],
            birthDeaths: [],
            predationEvents: [],
            environmentalEvents: [],
            resourceAvailability: [],
            equilibriumState: null
        };
        
        // Reset counters
        this.tickCount = 0;
        this.yearCount = 0;
        this.lastCensus = 0;
        
        console.log('[SIMULATION] Simulation reset');
    }
    
    changeSpeed(delta) {
        this.simulationSpeed = Math.max(0.1, Math.min(5.0, this.simulationSpeed + delta));
        console.log(`[SIMULATION] Speed changed to ${this.simulationSpeed}x`);
    }
    
    showAnalytics() {
        this.analyticsPanel.visible = true;
        this.updateAnalyticsDisplay();
    }
    
    hideAnalytics() {
        this.analyticsPanel.visible = false;
    }
    
    updateAnalyticsDisplay() {
        // Update statistics text
        const latest = this.analytics.populationHistory[this.analytics.populationHistory.length - 1];
        let statsText = 'CURRENT POPULATIONS:\n\n';
        
        if (latest) {
            for (const [species, count] of Object.entries(latest.populations)) {
                statsText += `${species}: ${count}\n`;
            }
        }
        
        statsText += '\nRECENT EVENTS:\n\n';
        
        // Show recent predation events
        const recentPredations = this.analytics.predationEvents.slice(-5);
        for (const event of recentPredations) {
            statsText += `${event.predator} hunted ${event.prey}\n`;
        }
        
        // Show recent environmental events
        const recentEnvEvents = this.analytics.environmentalEvents.slice(-3);
        for (const event of recentEnvEvents) {
            statsText += `Year ${event.year}: ${event.event}\n`;
        }
        
        this.statsText.text = statsText;
        
        // Update equilibrium status
        if (this.analytics.equilibriumState) {
            this.equilibriumText.text = `EQUILIBRIUM ACHIEVED!\nYear: ${this.analytics.equilibriumState.year}\nTick: ${this.analytics.equilibriumState.tick}`;
            this.equilibriumText.style.fill = 0x00FF00;
        } else {
            this.equilibriumText.text = 'EQUILIBRIUM: NOT YET ACHIEVED';
            this.equilibriumText.style.fill = 0xFF0000;
        }
        
        // Update population chart
        this.updatePopulationChart();
    }
    
    updatePopulationChart() {
        this.populationLines.clear();
        
        if (this.analytics.populationHistory.length < 2) return;
        
        const chartWidth = 340;
        const chartHeight = 180;
        const chartX = 350;
        const chartY = 80;
        
        const species = ['RABBIT', 'CHICKEN', 'WOLF', 'HAWK', 'DEER', 'GOAT'];
        const colors = [0xFF0000, 0xFFFF00, 0x0000FF, 0xFF00FF, 0x00FF00, 0xFF8800];
        
        for (let i = 0; i < species.length; i++) {
            const speciesName = species[i];
            const color = colors[i];
            
            this.populationLines.setStrokeStyle({ width: 2, color: color });
            
            let firstPoint = true;
            
            for (let j = 0; j < this.analytics.populationHistory.length; j++) {
                const data = this.analytics.populationHistory[j];
                const count = data.populations[speciesName] || 0;
                
                const x = chartX + (j / (this.analytics.populationHistory.length - 1)) * chartWidth;
                const y = chartY + chartHeight - (count / 50) * chartHeight; // Scale to max 50
                
                if (firstPoint) {
                    this.populationLines.moveTo(x, y);
                    firstPoint = false;
                } else {
                    this.populationLines.lineTo(x, y);
                }
            }
        }
    }
    
    togglePlacementMode() {
        this.placementModeActive = !this.placementModeActive;
        
        if (this.placementModeActive) {
            this.placementModeBtn.children[1].text = 'PLACE MODE: ON';
            this.placementModeBtn.children[1].style.fill = 0x00FF00;
            
            // Enable click listener for placement
            this.app.stage.eventMode = 'static';
            this.app.stage.on('pointerdown', this.handlePlacementClick);
            
            console.log('[SIMULATION] Placement mode activated');
        } else {
            this.placementModeBtn.children[1].text = 'PLACE MODE: OFF';
            this.placementModeBtn.children[1].style.fill = 0x888888;
            
            // Disable click listener
            this.app.stage.off('pointerdown', this.handlePlacementClick);
            
            console.log('[SIMULATION] Placement mode deactivated');
        }
    }
    
    handlePlacementClick = (event) => {
        if (!this.placementModeActive || !this.selectedSpecies) return;
        
        const position = event.global;
        
        // Create new animal at clicked position
        const animal = addEntity(this.world);
        Animal.type[animal] = this.getAnimalTypeId(this.selectedSpecies);
        Animal.energy[animal] = 75;
        Animal.x[animal] = position.x;
        Animal.y[animal] = position.y;
        
        console.log(`[SIMULATION] Placed ${this.selectedSpecies} at (${position.x.toFixed(0)}, ${position.y.toFixed(0)})`);
    }
    
    cleanup() {
        this.app.ticker.remove(this.update, this);
        
        // Remove UI elements
        this.app.stage.removeChild(this.controlPanel);
        this.app.stage.removeChild(this.analyticsPanel);
        this.app.stage.removeChild(this.placementPanel);
        
        // Cleanup containers
        this.controlPanel.destroy({ children: true });
        this.analyticsPanel.destroy({ children: true });
        this.placementPanel.destroy({ children: true });
    }
}
